// Admin card on /integrations — masked view of the current Resend key,
// paste-in field to rotate, and a "Test" button that fires a live SMTP send
// so you can verify a new key immediately.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Send, KeyRound, ExternalLink, Trash2, RefreshCw } from "lucide-react";

const SOURCE_BADGE = {
  db:   { label: "DATABASE", cls: "bg-cyan-50 text-cyan-700 border-cyan-300" },
  env:  { label: "ENV VAR", cls: "bg-slate-50 text-slate-700 border-slate-300" },
  none: { label: "NOT SET", cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

export default function ResendSettingsCard() {
  const qc = useQueryClient();
  const [newKey, setNewKey] = useState("");
  const [sender, setSender] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [showSaveKey, setShowSaveKey] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["resend-settings"],
    queryFn: async () => (await api.get("/settings/resend")).data,
  });

  const save = useMutation({
    mutationFn: async (payload) => (await api.put("/settings/resend", payload)).data,
    onSuccess: () => {
      toast.success("Resend settings saved");
      setNewKey(""); setSender(""); setShowSaveKey(false);
      qc.invalidateQueries({ queryKey: ["resend-settings"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const clear = useMutation({
    mutationFn: async () => (await api.delete("/settings/resend")).data,
    onSuccess: () => { toast.success("DB key cleared — falling back to env"); refetch(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Clear failed"),
  });

  const test = useMutation({
    mutationFn: async () => (await api.post("/settings/resend/test", { to: testEmail })).data,
    onSuccess: (r) => {
      setTestResult(r);
      if (r.status === "sent") toast.success(`Test email delivered to ${testEmail}`);
      else if (r.status === "mocked") toast.warning("No key configured — send was mocked (logged only)");
      else toast.error(`Send failed: ${r.error || "unknown"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Test send failed"),
  });

  if (isLoading || !data) return null;
  const src = SOURCE_BADGE[data.source] || SOURCE_BADGE.none;

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow p-5 space-y-4" data-testid="resend-settings-card">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <KeyRound size={18} className="text-cyan-600" /> Resend delivery
            </div>
            <Badge className={`rounded-sm border font-normal ${src.cls}`} data-testid="resend-source-badge">{src.label}</Badge>
            {!data.has_key && <Badge className="rounded-sm border font-normal bg-amber-50 text-amber-700 border-amber-300">MOCK MODE</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Change the API key without redeploying. DB-stored keys override <code>.env</code> so rotations take effect instantly.{" "}
            <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline inline-flex items-center gap-0.5">
              Get a key <ExternalLink size={10} />
            </a>
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()} className="rounded-sm h-8" data-testid="resend-refresh-btn">
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
      </header>

      {/* Current config */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[1.6px] text-slate-500 font-semibold mb-1">API key</div>
          <div className="font-mono text-sm text-slate-800" data-testid="resend-key-masked">{data.key_masked || "(none)"}</div>
          <div className="text-[10px] text-slate-400 mt-1">Source: {data.source}</div>
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[1.6px] text-slate-500 font-semibold mb-1">Sender</div>
          <div className="text-sm text-slate-800 truncate" data-testid="resend-sender">{data.sender}</div>
          <div className="text-[10px] text-slate-400 mt-1">SMTP {data.smtp_host}:{data.smtp_port}</div>
        </div>
      </div>

      {/* Test send */}
      <div className="border border-slate-200 rounded-sm p-3 space-y-2">
        <Label className="text-[10px] uppercase tracking-[1.6px] text-slate-500 font-semibold">Test delivery</Label>
        <div className="flex gap-2 flex-wrap">
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} type="email"
            placeholder="you@example.com" className="rounded-sm h-9 flex-1 min-w-[200px]"
            data-testid="resend-test-email" />
          <Button onClick={() => test.mutate()} disabled={test.isPending || !testEmail || testEmail.length < 5}
            className="rounded-sm h-9 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="resend-test-btn">
            {test.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />}
            Send test
          </Button>
        </div>
        {testResult && (
          <div className={`text-xs px-2.5 py-2 rounded-sm border flex items-start gap-2 ${
            testResult.status === "sent" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : testResult.status === "mocked" ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-rose-50 border-rose-200 text-rose-800"
          }`} data-testid="resend-test-result">
            {testResult.status === "sent" ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> :
             testResult.status === "mocked" ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> :
             <XCircle size={14} className="mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold">Status: {testResult.status}</div>
              {testResult.id && <div className="font-mono text-[10px] mt-0.5 truncate">Message id: {testResult.id}</div>}
              {testResult.error && <div className="text-[11px] mt-0.5">{testResult.error}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Rotate key */}
      {!showSaveKey ? (
        <Button variant="outline" onClick={() => setShowSaveKey(true)} className="rounded-sm h-9" data-testid="resend-rotate-btn">
          <KeyRound size={13} className="mr-1.5" /> Rotate API key
        </Button>
      ) : (
        <div className="border-2 border-cyan-200 bg-cyan-50/30 rounded-sm p-3 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[1.6px] text-slate-500 font-semibold">New Resend API key</Label>
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} type="password" autoComplete="off"
              placeholder="re_..." className="rounded-sm h-9 font-mono mt-1"
              data-testid="resend-new-key-input" />
            <div className="text-[10px] text-slate-500 mt-1">
              Grab a fresh key from{" "}
              <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">resend.com/api-keys</a>.
              Full send access is required for quote deliveries.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[1.6px] text-slate-500 font-semibold">Sender email (optional)</Label>
            <Input value={sender} onChange={(e) => setSender(e.target.value)} type="email"
              placeholder={data.sender} className="rounded-sm h-9 mt-1"
              data-testid="resend-sender-input" />
            <div className="text-[10px] text-slate-500 mt-1">Leave blank to keep the current sender. Must be a verified domain in Resend.</div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setShowSaveKey(false); setNewKey(""); setSender(""); }}
              className="rounded-sm" data-testid="resend-cancel-btn">Cancel</Button>
            <Button onClick={() => save.mutate({ ...(newKey ? {api_key: newKey} : {}), ...(sender ? {sender} : {}) })}
              disabled={save.isPending || (!newKey && !sender)}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="resend-save-btn">
              {save.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />}
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Danger — clear DB override */}
      {data.source === "db" && (
        <div className="border-t border-slate-100 pt-3">
          <button onClick={() => window.confirm("Clear the DB-stored key and fall back to .env?") && clear.mutate()}
            className="text-[11px] text-rose-600 hover:underline inline-flex items-center gap-1"
            data-testid="resend-clear-db-btn">
            <Trash2 size={11} /> Clear DB override (fall back to env)
          </button>
        </div>
      )}
    </Card>
  );
}
